import {
  byWindowAction,
  debounce,
  parameters,
  sendGoogleEvent,
} from '@html/_common/utils';

const SELECTOR_WRAPPER = '.js-ao-clp-carousel-slider';
const SELECT_ARROW_LEFT = '.js-ao-clp-carousel-slider__arrow--left';
const SELECT_ARROW_RIGHT = '.js-ao-clp-carousel-slider__arrow--right';
const SELECTOR_DOTS = '.js-ao-clp-carousel-slider__dots';
const CLASS_ACTIVE_DOT = 'ao-clp-carousel-slider__dots-dot--active';
const SELECTOR_CTA = '.js-ao-clp-carousel-slider__navigation-see-all-tours';
const DEFAULT_SELECTOR_SLIDER = '.js-ao-clp-carousel-slider__slider';

export default function AOClpCarouselSlider(uniqueSliderSelector) {
  const carousels = [];
  const SELECTOR_SLIDER = uniqueSliderSelector || DEFAULT_SELECTOR_SLIDER;

  let $carousels;
  let isTouchDevice;

  const checkDotsActivity = (carousel) => {
    const $activeDot = carousel.$dots.children().eq(carousel.index);
    $activeDot.addClass(CLASS_ACTIVE_DOT).siblings().removeClass(CLASS_ACTIVE_DOT);
  };

  const calculateDots = (carousel) => {
    carousel.$dots.html('');
    const numOfDots = Math.ceil(carousel.total / carousel.shown);

    if (!carousel.$dots.length || numOfDots < 2) return;

    for (let i = 0; i < numOfDots; i += 1) {
      carousel.$dots.append(`
      <li class="ao-clp-carousel-slider__dots-dot"></li>`);
    }

    carousel.$dots.removeClass('hid');
    checkDotsActivity(carousel);
  };

  const checkArrowActivity = (carousel) => {
    const isFirstCarouselItem = carousel.index === 0;
    const isLastCarouselItem = ((carousel.index * carousel.shown) + carousel.shown) >= carousel.total;

    const arrowLeft = carousel.$arrowElements.filter(SELECT_ARROW_LEFT);
    const arrowRight = carousel.$arrowElements.filter(SELECT_ARROW_RIGHT);

    arrowLeft.prop('disabled', isFirstCarouselItem);
    arrowRight.prop('disabled', isLastCarouselItem);

    const arrowsDisabled = arrowLeft.prop('disabled') && arrowRight.prop('disabled');

    arrowLeft.toggleClass('hid', (carousel.total <= carousel.shown || arrowsDisabled));
    arrowRight.toggleClass('hid', (carousel.total <= carousel.shown || arrowsDisabled));
  };

  const calculateShown = (carousel) => {
    const newShown = Math.round(carousel.$listElement.width() / carousel.elementWidth);

    carousel.index = Math.floor((carousel.shown * carousel.index) / newShown);
    carousel.shown = newShown;
  };

  const calculateElementDimensions = (carousel) => {
    carousel.elementWidth = carousel.$firstElement.width();
  };

  const onClickCTA = (event) => {
    const $target = $(event.currentTarget);
    const $carousel = $target.closest(SELECTOR_WRAPPER).find(SELECTOR_SLIDER);
    const carouselId = $carousel.data('carouselId');
    const carousel = carousels[carouselId];
    const targetLink = $target.attr('href');

    if (!targetLink) return;

    sendGoogleEvent(
      carousel.EVENT_CATEGORY,
      targetLink,
      carousel.EVENT_LABEL || 'Carousel Slider',
    );
  };

  const moveToStart = (carousel) => {
    carousel.$firstElement.css('margin-left', 0);
    carousel.index = 0;
  };

  const onResize = debounce(() => {
    for (let i = 0; i < carousels.length; i += 1) {
      const carousel = carousels[i];

      calculateElementDimensions(carousel);
      calculateShown(carousel);
      moveToStart(carousel);
      checkArrowActivity(carousel);
      calculateDots(carousel);
    }
  }, 250);

  const onNavigate = (event, id) => {
    let carouselId;

    if (event) {
      carouselId = $(event.target).data('carouselId');
    } else {
      carouselId = id;
    }

    const carousel = carousels[carouselId];

    if (!carousel.EVENT_LABEL) return;

    sendGoogleEvent(
      carousel.EVENT_CATEGORY,
      'Navigate',
      carousel.EVENT_LABEL,
    );
  };

  const initiateEvents = () => {
    $(SELECTOR_CTA).on('click', onClickCTA);

    if (isTouchDevice) $carousels.on('scroll', debounce(onNavigate, 300));
  };

  const moveCarousel = (carousel) => {
    let margin = -(carousel.index * carousel.shown
              * (carousel.elementWidth + carousel.elementMargin));
    const currentMargin = parseInt(carousel.$firstElement.css('margin-left'), 10);

    if (margin <= currentMargin) {
      const maxMargin = -((carousel.total - carousel.shown)
                  * (carousel.elementWidth + carousel.elementMargin));
      margin = Math.max(margin, maxMargin);
    } else {
      margin = Math.min(margin, 0);
    }

    carousel.$firstElement.css('margin-left', margin);
  };

  const onClickArrow = (event) => {
    const $arrow = $(event.currentTarget);
    const carouselId = $arrow.data('carouselId');
    const carousel = carousels[carouselId];
    const arrowDirection = $arrow.hasClass('js-ao-clp-carousel-slider__arrow--right') ? 'right' : 'left';

    if (arrowDirection === 'right') {
      carousel.index += 1;
    } else {
      carousel.index -= 1;
    }

    moveCarousel(carousel);
    checkArrowActivity(carousel);
    checkDotsActivity(carousel);

    onNavigate(null, carouselId);

    sendGoogleEvent(
      carousel.EVENT_CATEGORY,
      'arrows',
      arrowDirection,
    );
  };

  const initiateCarousel = (index, element) => {
    const $carouselElement = $(element);

    const carouselId = carousels.length;
    const carousel = {};
    carousel.index = 0;
    carousel.shown = 1;
    carousel.total = $carouselElement.children().length;
    carousel.elementMargin = 0;
    carousel.$listElement = $carouselElement;
    carousel.$firstElement = $carouselElement.children().first();
    carousel.$arrowElements = $carouselElement.parent().find('.js-ao-clp-carousel-slider__arrow');
    carousel.$dots = $carouselElement.parent().find(SELECTOR_DOTS);
    carousel.EVENT_CATEGORY = $carouselElement.data('event-category');
    carousel.EVENT_LABEL = $carouselElement.data('event-label');

    $carouselElement.data('carouselId', carouselId);

    calculateElementDimensions(carousel);

    carousel.$arrowElements.each((arrowIndex, arrowElement) => {
      const $arrowElement = $(arrowElement);
      $arrowElement.data('carouselId', carouselId);
      $arrowElement.off('click').on('click', onClickArrow);
    });

    calculateShown(carousel);
    checkArrowActivity(carousel);
    calculateDots(carousel);

    carousels.push(carousel);
  };

  const initiate = () => {
    $carousels = $(SELECTOR_SLIDER);

    if (!$carousels.length) return;

    isTouchDevice = ['mobile', 'tablet'].indexOf(parameters.deviceType) >= 0;

    $carousels.each(initiateCarousel);

    initiateEvents();

    byWindowAction(['resize'], onResize);
  };

  return { initiate };
}
